import React, { useEffect, useMemo, useState } from 'react';
import {
  useAppStateApartmentCompareListStore,
  useAppStateAssetReportListStore,
  useAppStateManageApartmentModalDataStore,
} from '../../../common/store/AppStateStore.zustand';
import { ApartmentCompare, ApartmentCompareChartData } from '../type/ApartmentCompare.type';
import { useApartmentCompareAPI } from '../api/ApartmentCompare.api';
import { buildApartmentCompareChartData } from '../util/ApartmentCompareUtils';
import ApartmentCompareCardLineChart from './ApartmentCompareCardLineChart';
import ManageApartmentModal from './popup/ManageApartmentModal';

const ApartmentCompareCard = () => {
  const { apartmentCompareList } = useAppStateApartmentCompareListStore();
  const { assetReportList } = useAppStateAssetReportListStore();
  const { manageApartmentModalData, setManageApartmentModalData } = useAppStateManageApartmentModalDataStore();
  const { fetchApartmentTransactions } = useApartmentCompareAPI();

  const [currentApt, setCurrentApt] = useState<ApartmentCompare | undefined>();
  const [chartDataList, setChartDataList] = useState<ApartmentCompareChartData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  // 목록이 바뀌면 첫 번째 항목으로 초기화
  useEffect(() => {
    if (apartmentCompareList && apartmentCompareList.length > 0) {
      setCurrentApt(apartmentCompareList[0]);
    } else {
      setCurrentApt(undefined);
      setChartDataList([]);
    }
  }, [apartmentCompareList]);

  // 선택된 아파트 또는 자산 데이터가 바뀌면 차트 데이터 로드
  useEffect(() => {
    if (!currentApt || !assetReportList || assetReportList.length === 0) {
      setChartDataList([]);
      return;
    }
    loadChartData(currentApt);
  }, [currentApt, assetReportList]);

  const loadChartData = async (apt: ApartmentCompare) => {
    setIsLoading(true);
    setLoadError('');
    try {
      const yearMonthList = assetReportList.map((r) => r.yearMonth);
      const transactions = await fetchApartmentTransactions(apt, yearMonthList);
      const data = buildApartmentCompareChartData(assetReportList, transactions);
      setChartDataList(data);
    } catch {
      setLoadError('미리 생성된 실거래가 데이터를 불러오지 못했습니다.');
      setChartDataList([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAptSelect = (id: string) => {
    const found = apartmentCompareList.find((a) => a.id === id);
    setCurrentApt(found);
  };

  const handleManageClick = () => {
    setManageApartmentModalData({ isOpen: true, onClose: () => setManageApartmentModalData(undefined) });
  };

  const yearMonthRange = useMemo(() => {
    if (!assetReportList || assetReportList.length === 0) return null;
    const sorted = [...assetReportList].sort((a, b) => a.yearMonth.localeCompare(b.yearMonth));
    return { start: sorted[0].yearMonth, end: sorted[sorted.length - 1].yearMonth };
  }, [assetReportList]);

  const hasTransactionData = chartDataList.some((d) => d.aptPrice !== undefined);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-300 group relative overflow-hidden">
      {/* 배경 장식 */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/5 rounded-full -translate-y-32 translate-x-32 group-hover:translate-y-[-8rem] transition-transform duration-500"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-amber-500/5 rounded-full translate-y-32 -translate-x-32 group-hover:translate-y-[8rem] transition-transform duration-500"></div>

      {/* 헤더 */}
      <div className="flex justify-between items-center mb-6 relative">
        <div>
          <h3 className="text-xl font-semibold text-gray-800 mb-1">아파트 실거래가 비교</h3>
          <p className="text-sm text-gray-500">순자산과 대표면적 실거래 추정가 비교</p>
        </div>
        <div className="relative">
          <select
            value={currentApt?.id ?? ''}
            onChange={(e) => handleAptSelect(e.target.value)}
            className="appearance-none bg-white border border-gray-200 rounded-lg py-2 px-4 pr-8 text-gray-700 shadow-sm hover:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100 focus:border-orange-500 cursor-pointer !rounded-button whitespace-nowrap transition-all duration-200"
          >
            {apartmentCompareList.map((apt) => (
              <option key={apt.id} value={apt.id}>{apt.name}</option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
            <i className="fas fa-chevron-down text-xs"></i>
          </div>
        </div>
      </div>

      {/* 비어있는 상태 */}
      {(!currentApt || apartmentCompareList.length === 0) && (
        <div className="flex flex-col items-center justify-center h-64 bg-gray-50/50 rounded-lg p-6">
          <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mb-4">
            <i className="fas fa-building text-3xl text-orange-300"></i>
          </div>
          <h4 className="text-lg font-medium text-gray-700 mb-2">등록된 아파트가 없습니다</h4>
          <p className="text-sm text-gray-500 text-center max-w-sm">비교할 아파트를 추가하고 순자산과 비교해보세요</p>
          <button
            className="mt-6 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-all duration-200 flex items-center space-x-2 !rounded-button whitespace-nowrap"
            onClick={handleManageClick}
          >
            <i className="fas fa-plus"></i>
            <span>아파트 추가하기</span>
          </button>
        </div>
      )}

      {/* 데이터 있는 상태 */}
      {currentApt && (
        <>
          {/* 기간 / 관리 버튼 */}
          <div className="flex items-center justify-between mb-4">
            {yearMonthRange && (
              <div className="bg-orange-50 text-orange-700 px-4 py-1.5 rounded-full text-sm font-medium">
                {yearMonthRange.start} ~ {yearMonthRange.end}
              </div>
            )}
            <button
              onClick={handleManageClick}
              className="text-sm text-gray-400 hover:text-gray-600 flex items-center space-x-1"
            >
              <i className="fas fa-cog"></i>
              <span>관리</span>
            </button>
          </div>

          {/* 아파트 정보 배지 */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{currentApt.sidoName} {currentApt.sigunguName}</p>
                <p className="font-medium text-gray-800">{currentApt.aptName} <span className="text-gray-500 font-normal">{currentApt.excluUseAr}㎡</span></p>
              </div>
              <div className="flex items-center space-x-1">
                <span className="w-3 h-3 rounded-full bg-orange-400 inline-block"></span>
                <span className="text-xs text-gray-500">대표면적 추정가</span>
                <span className="w-3 h-3 rounded-full bg-blue-500 inline-block ml-2"></span>
                <span className="text-xs text-gray-500">순자산</span>
              </div>
            </div>
          </div>

          {/* 로딩 */}
          {isLoading && (
            <div className="flex items-center justify-center h-72 text-gray-400">
              <i className="fas fa-spinner fa-spin text-2xl mr-3"></i>
              <span>실거래가 데이터 불러오는 중...</span>
            </div>
          )}

          {/* 에러 */}
          {!isLoading && loadError && (
            <div className="flex flex-col items-center justify-center h-72 text-red-400">
              <i className="fas fa-exclamation-circle text-3xl mb-3"></i>
              <p className="text-sm text-center">{loadError}</p>
            </div>
          )}

          {/* 거래 데이터 없음 */}
          {!isLoading && !loadError && !hasTransactionData && chartDataList.length > 0 && (
            <div className="flex flex-col items-center justify-center h-72 text-gray-400">
              <i className="fas fa-search text-3xl mb-3"></i>
              <p className="text-sm">해당 기간에 이 아파트의 실거래 내역이 없습니다.</p>
            </div>
          )}

          {/* 차트 */}
          {!isLoading && !loadError && hasTransactionData && (
            <ApartmentCompareCardLineChart chartDataList={chartDataList} aptName={currentApt.name} />
          )}
        </>
      )}

      {manageApartmentModalData && <ManageApartmentModal {...manageApartmentModalData} />}
    </div>
  );
};

export default ApartmentCompareCard;
